import { Autocomplete, TextField, createFilterOptions } from '@mui/material';

interface Props {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Offer a typed value that is not in the list as “Add …”. */
  allowCreate?: boolean;
  /** Focus and open the list on mount. */
  autoFocus?: boolean;
}

const ADD_PREFIX = 'Add "';
const filter = createFilterOptions<string>();

const unwrap = (raw: string) =>
  raw.startsWith(ADD_PREFIX) && raw.endsWith('"')
    ? raw.slice(ADD_PREFIX.length, -1)
    : raw;

/** Pick-list with type-to-filter; the typed text is the value, so clearing and resets stay in sync. */
export default function ValuePicker({
  value,
  options,
  onChange,
  placeholder,
  size = 'medium',
  disabled,
  allowCreate = true,
  autoFocus,
}: Props) {
  return (
    <Autocomplete
      fullWidth
      // form.css lifts the focused outline to z-index 5, which would cover the clear button
      sx={{ '& .MuiAutocomplete-endAdornment': { zIndex: 6 } }}
      openOnFocus={autoFocus}
      freeSolo={allowCreate}
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      size={size}
      disabled={disabled}
      options={options}
      value={value || null}
      inputValue={value}
      onChange={(_, next) =>
        onChange(typeof next === 'string' ? unwrap(next) : '')
      }
      onInputChange={(_, text) => onChange(unwrap(text))}
      filterOptions={(candidates, params) => {
        const filtered = filter(candidates, params);
        const typed = params.inputValue.trim();
        const exists = candidates.some(
          c => c.toLowerCase() === typed.toLowerCase(),
        );
        if (allowCreate && typed && !exists) {
          filtered.push(`${ADD_PREFIX}${typed}"`);
        }
        return filtered;
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
      )}
    />
  );
}
